import type { OverflowMenuAction } from '../ui';
import { Button } from '../ui';

// ONE renderer for a `buildFileActions` entry as a button. Three surfaces render that action set as buttons
// rather than as a menu — the viewer modal's footer, the standalone file page's footer, and the no-viewer
// fallback panel — and before this they each did it themselves:
//
//   - the modal and the standalone page carried the SAME `action.href ? <Button href> : <Button onClick>`
//     ternary, duplicated verbatim;
//   - `FileDetailBody` had only the `onClick` half, so it SILENTLY DROPPED any `href` and rendered a dead
//     button. That was latent while `kind === null` actions had no href — and became the common case the
//     moment "Open in new tab" turned into a real anchor.
//
// So the duplication was not merely untidy, it was already a bug in one of the three copies.
export function FileActionButton({ action, onBi }: { action: OverflowMenuAction; onBi: (key: string) => void }) {
  const fire = () => {
    onBi(action.key);
    action.onSelect?.();
  };

  if (action.href) {
    const newTab = action.external || action.newTab;
    return (
      <Button
        variant="outlined"
        startIcon={action.icon}
        href={action.href}
        // `external` on `Button` means "render a plain <a> and do NOT prefix the mount", which is what every
        // href from `buildFileActions` needs — they arrive already resolved (`fileNewTabLink` applies the
        // prefix, signed media URLs are absolute). Without it the `<Link>` branch prepends `basePath` a
        // SECOND time, producing `/orion-web/orion-web/…` on the legacy mounts.
        external
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        // Never `preventDefault` — the anchor's own navigation is the action. `onClick` is the BI hook only,
        // which is also why a middle-click goes unreported (browsers fire `auxclick`, not `click`).
        onClick={fire}
      >
        {action.label}
      </Button>
    );
  }

  return (
    <Button variant="outlined" startIcon={action.icon} onClick={fire}>
      {action.label}
    </Button>
  );
}
